import Phaser from "phaser";
import { CANVAS_COLOURS, cssColour, type EntityInstance } from "daedalus-core";

/**
 * Entity markers: a diamond per entity of an entity layer labelled with its type's name, the selected one
 * highlighted. Entities have no sprite of their own, so the editor needs a marker to find and select
 * them, and that name sits under the marker so an object's own art, which rises from the point,
 * stays readable.
 */
export class EntityMarkers {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly labels = new Map<string, Phaser.GameObjects.Text>();
  private signature = "";

  /**
   * @param scene Scene to draw into.
   * @param tileSize Tile size in pixels.
   * @param depth Depth above chunk layers.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly tileSize: number,
    depth: number,
  ) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * Redraws when the entity list, visibility or selection changed.
   *
   * @param entities The layer's entities.
   * @param visible Whether the layer is shown.
   * @param selected The selected entity's id, a test naming every selected one (a marker marquee selects
   *   many, P1.6-T33), or null for none.
   * @param labelOf The name a type is shown by, its registered `label` (ADR-070 Appendix A.2); the type's id
   *   when absent.
   */
  public sync(entities: readonly EntityInstance[], visible: boolean, selected: string | ((id: string) => boolean) | null, labelOf: (type: string) => string = (type) => type): void {
    const isSelected = typeof selected === "function" ? selected : (id: string): boolean => id === selected;
    const signature = `${visible}|${entities.map((e) => `${e.id}:${isSelected(e.id) ? "*" : ""}:${labelOf(e.type)}:${e.x}:${e.y}`).join(",")}`;
    if (signature === this.signature) {
      return;
    }
    this.signature = signature;
    const g = this.graphics;
    g.clear();
    const seen = new Set<string>();
    for (const entity of entities) {
      seen.add(entity.id);
      const x = entity.x * this.tileSize;
      const y = entity.y * this.tileSize;
      const r = this.tileSize * 0.35;
      const colour = isSelected(entity.id) ? CANVAS_COLOURS.selectedEntity : CANVAS_COLOURS.entity;
      g.fillStyle(colour.rgb, visible ? colour.alpha * 0.85 : 0);
      g.fillTriangle(x, y - r, x + r, y, x - r, y);
      g.fillTriangle(x - r, y, x + r, y, x, y + r);
      g.lineStyle(2, CANVAS_COLOURS.entityOutline.rgb, visible ? CANVAS_COLOURS.entityOutline.alpha * 0.8 : 0);
      g.strokeCircle(x, y, r);
      let label = this.labels.get(entity.id);
      if (label === undefined) {
        label = this.scene.add.text(0, 0, labelOf(entity.type), { fontFamily: "monospace", fontSize: "11px", color: cssColour(CANVAS_COLOURS.entityLabel), backgroundColor: cssColour(CANVAS_COLOURS.entityLabelBackground), padding: { x: 2, y: 1 } }).setOrigin(0.5, 0).setDepth(this.graphics.depth + 1);
        this.labels.set(entity.id, label);
      }
      label.setText(labelOf(entity.type)).setPosition(x, y + r + 2).setVisible(visible);
    }
    for (const [id, label] of this.labels) {
      if (!seen.has(id)) {
        label.destroy();
        this.labels.delete(id);
      }
    }
  }

  /**
   * Removes the markers.
   */
  public destroy(): void {
    this.graphics.destroy();
    for (const label of this.labels.values()) {
      label.destroy();
    }
    this.labels.clear();
  }
}

